import { ApiService } from '../ApiService';
import type {
  ReconcilePayload,
  ReconcileResult,
  UnreconciledResponse,
  UnreconciledParams,
  ReconciliationGroups,
  UnreconcileResult,
  AutoMatchPayload,
  AutoMatchResult,
  UnletteredBalanceResult,
} from '../../../types/reconciliation';

/**
 * Endpoints de lettrage comptable — préfixe /ledger/reconciliation
 * Source: API DOCUMENTATION/reconciliation.md
 */
export const reconciliationApi = {
  /**
   * POST /ledger/reconciliation — Lettrer des lignes
   * Assigne un matchingNumber commun aux lignes sélectionnées
   */
  reconcile: (payload: ReconcilePayload) =>
    ApiService.post<{ success: boolean; data: ReconcileResult }>(
      '/ledger/reconciliation', 
      payload
    ),

  /**
   * DELETE /ledger/reconciliation/:matchingNumber — Dé-lettrer
   * Supprime le matchingNumber de toutes les lignes du groupe
   */
  unreconcile: (matchingNumber: string) =>
    ApiService.delete<{ success: boolean; data: UnreconcileResult }>(
      `/ledger/reconciliation/${encodeURIComponent(matchingNumber)}`
    ),

  /**
   * GET /ledger/reconciliation/accounts/:accountId/unreconciled — Lignes non lettrées
   * Retourne les lignes POSTED sans matchingNumber
   */
  getUnreconciled: (accountId: string, params?: UnreconciledParams) =>
    ApiService.get<{ success: boolean; data: UnreconciledResponse }>(
      `/ledger/reconciliation/accounts/${accountId}/unreconciled`,
      params
    ),

  /**
   * GET /ledger/reconciliation/accounts/:accountId/groups — Groupes lettrés
   * Retourne les lignes lettrées groupées par matchingNumber
   */
  getGroups: (accountId: string) =>
    ApiService.get<{ success: boolean; data: ReconciliationGroups }>(
      `/ledger/reconciliation/accounts/${accountId}/groups`
    ),

  /**
   * POST /ledger/reconciliation/auto-match — Lettrage automatique
   *
   * Stratégies appliquées par le backend :
   *  1) lignes partageant la même référence dont la balance est nulle ;
   *  2) paires débit/crédit de même montant priorisées par proximité de date.
   * Retourne le nombre de lignes lettrées et le restant.
   */
  autoMatch: (payload: AutoMatchPayload) =>
    ApiService.post<{ success: boolean; data: AutoMatchResult }>(
      '/ledger/reconciliation/auto-match',
      payload,
    ),

  /**
   * GET /ledger/reconciliation/accounts/:accountId/unlettered-balance — Solde non lettré
   *
   * Somme algébrique débit − crédit des lignes non lettrées du compte.
   */
  getUnletteredBalance: (accountId: string, asOfDate?: string) =>
    ApiService.get<{ success: boolean; data: UnletteredBalanceResult }>(
      `/ledger/reconciliation/accounts/${accountId}/unlettered-balance`,
      asOfDate ? { asOfDate } : undefined,
    ),
};
